import { type FC } from "react";
import { useTranslation } from "react-i18next";
import type { SalesAttributionOverlapStats } from "../utils/salesAttributionTypes";
import OverlapVennChart from "./OverlapVennChart";
import { panelTooltip, SalesAttributionPanel } from "./salesAttributionShared";

interface SalesAttributionOverlapSectionProps {
  overlap: SalesAttributionOverlapStats;
}

export const SalesAttributionOverlapSection: FC<
  SalesAttributionOverlapSectionProps
> = ({ overlap }) => {
  const { t } = useTranslation();

  return (
    <SalesAttributionPanel
      title={t("salesAttribution.ui.panelTitles.overlap", {
        defaultValue: "Platform overlapping revenue and orders",
      })}
      definition={panelTooltip(
        t,
        "overlap",
        "Shows Shopify orders and revenue that were attributed to Google Ads, Meta Ads, or both platforms based on customer interactions before purchase. An order is considered claimed by a platform when the customer interacted with that platform during the tracked customer journey leading to the purchase."
      )}
      dataPdfBlock="attribution-overlap"
    >

      <div className="sales-attribution__venn-grid">
        <OverlapVennChart
          title={t("salesAttribution.ui.overlapOrdersTitle", { defaultValue: "Overlapping orders" })}
          stats={overlap.orders}
          definition={panelTooltip(
            t,
            "overlapOrders",
            "Breakdown of Shopify orders attributed exclusively to Google Ads, exclusively to Meta Ads, or to both platforms based on tracked customer interactions before purchase."
          )}
        />
        <OverlapVennChart
          title={t("salesAttribution.ui.overlapRevenueTitle", { defaultValue: "Overlapping revenue" })}
          stats={overlap.revenue}
          definition={panelTooltip(
            t,
            "overlapRevenue",
            "Breakdown of Shopify revenue from orders attributed exclusively to Google Ads, exclusively to Meta Ads, or to both platforms based on tracked customer interactions before purchase."
          )}
        />
      </div>
    </SalesAttributionPanel>
  );
};

export default SalesAttributionOverlapSection;
